import React, { useState, useEffect } from 'react';
import { Button, FormGroup, Label, Input, Row, Col } from 'reactstrap';
import { H5 } from '../../../../AbstractElements';
import { fetchFormData, createExpertReport } from '../../../../Api/ApiMethods';
import { toast } from 'react-toastify';
import { useParams } from 'react-router-dom';

const ExpertReportForm = ({ formData, errors, setFormData, workFileTypes, fieldPermissions, canEdit = true }) => {
    const { id } = useParams(); // Get call form ID
    const [reportTemplates, setReportTemplates] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState('');
    const [selectedFileNumber, setSelectedFileNumber] = useState('');
    const [loadingTemplates, setLoadingTemplates] = useState(false);
    const [creatingReport, setCreatingReport] = useState(false);

    const fileTemplateApiUrl = process.env.REACT_APP_LACIVERT_API_ENDPOINT + "/FileTemplate";
    const createReportApiUrl = process.env.REACT_APP_LACIVERT_API_ENDPOINT + "/Report/create-report";
    // Fetch report templates when component mounts
    useEffect(() => {
        const fetchReportTemplates = async () => {
            setLoadingTemplates(true);
            try {
                const response = await fetchFormData({
                    apiEndpoint: fileTemplateApiUrl,
                    page: 1,
                    pageSize: 100,
                    search: null,
                });

                console.log('Report templates API response:', response);

                let templates = [];
                if (response && response.isSuccess && response.data && Array.isArray(response.data.items)) {
                    templates = response.data.items.filter(template => template.isExpertReport === true);
                } else if (response && Array.isArray(response.items)) {
                    templates = response.items.filter(template => template.isExpertReport === true);
                } else if (Array.isArray(response)) {
                    templates = response.filter(template => template.isExpertReport === true);
                }

                setReportTemplates(templates);
                console.log('Eksper rapor şablonları yüklendi:', templates);
            } catch (error) {
                console.error('Error fetching report templates:', error);
                toast.error('Rapor şablonları yüklenirken bir hata oluştu.');
                setReportTemplates([]);
            } finally {
                setLoadingTemplates(false);
            }
        };

        fetchReportTemplates();
    }, [fileTemplateApiUrl]);

    // Helper function to check field access based on permissions
    const getFieldAccess = (fieldName) => {
        // If canEdit is false, all fields should be disabled
        if (!canEdit) {
            return {
                disabled: true,
                required: false,
            };
        }

        // Ensure fieldPermissions is an array before calling .find()
        const safeFieldPermissions = Array.isArray(fieldPermissions) ? fieldPermissions : [];
        // Case-insensitive comparison for robustness
        const permission = safeFieldPermissions.find(p => p.fieldName.toLowerCase() === fieldName.toLowerCase());
        // Return 'disabled' directly for easier use
        return {
            // Set disabled to true if not editable (ReadOnly) or not found (default to not editable for safety)
            disabled: permission ? permission.access !== 'Editable' : true,
            required: permission ? permission.required : false,
        };
    };    // Helper function to check if file type is LOR (Hak Mahrumiyeti)
    const isLorFileType = (title) => {
        if (!title) return false;
        const lowerTitle = title.toLowerCase();
        return lowerTitle.includes('hak mahrumiyeti') ||
               lowerTitle.includes('lor') ||
               lowerTitle.includes('mahrumiyeti');
    };

    // Get available file numbers from formData.filesToBeWorked
    const getAvailableFileNumbers = () => {
        if (!formData.filesToBeWorked || formData.filesToBeWorked.length === 0) {
            return [];
        }

        return formData.filesToBeWorked.map((accidentFileEntry, index) => {
            const fileNumber = accidentFileEntry.fileNumber || (accidentFileEntry.accidentFileId ? 
                (() => {
                    const accidentFile = formData.accidentFiles?.find(af => af.id === accidentFileEntry.accidentFileId);
                    return accidentFile ? accidentFile.fileNumber : accidentFileEntry.accidentFileId;
                })() : 'Yeni'
            );

            // Get file type titles for this entry
            let allFileTypeTitles = [];
            
            // Handle old format (from handleAdd)
            if (accidentFileEntry.fileType && !accidentFileEntry.filesToBeWorked) {
                allFileTypeTitles = accidentFileEntry._fileTypeTitles || [];
            } else {
                // Handle new format (from API)
                accidentFileEntry.filesToBeWorked?.forEach(file => {
                    if (file._fileTypeTitles && Array.isArray(file._fileTypeTitles)) {
                        allFileTypeTitles.push(...file._fileTypeTitles);
                    }
                });
            }

            // Check if this file has non-LOR types (eksper raporu should be for non-LOR files)
            const hasNonLorTypes = allFileTypeTitles.some(title => !isLorFileType(title));
            
            // Skip files that only contain LOR types (cannot create reports for LOR-only files)
            if (!hasNonLorTypes && allFileTypeTitles.length > 0) {
                return null;
            }
            
            return {
                value: accidentFileEntry.accidentFileId || index.toString(), // Use accidentFileId, fallback to index
                label: fileNumber,
                accidentFileId: accidentFileEntry.accidentFileId,
                hasNonLorTypes: hasNonLorTypes,
                fileTypeTitles: allFileTypeTitles
            };
        }).filter(item => item !== null); // Filter out null entries (LOR-only files)
    };

    // Define field access for file number selection and report generation
    const selectFileNumberAccess = getFieldAccess('expertreport.generate');
    const generateReportAccess = getFieldAccess('expertreport.generate');    // Handler for creating reports
    const handleCreateReport = async () => {
        if (!selectedFileNumber) {
            toast.error('Lütfen bir dosya numarası seçin.');
            return;
        }
        
        if (!selectedTemplate) {
            toast.error('Lütfen bir rapor şablonu seçin.');
            return;
        }

        if (!id) {
            toast.error('Çağrı formu ID bulunamadı.');
            return;
        }

        setCreatingReport(true);
        try {
            await createExpertReport({
                apiEndpoint: createReportApiUrl,
                callFormId: id,
                reportTemplateId: selectedTemplate,
                accidentFileId: selectedFileNumber // Send accidentFileId to API
            });
            
            setSelectedTemplate(''); // Reset selection after successful creation
            setSelectedFileNumber(''); // Reset file number selection
        } catch (error) {
            console.error('Error creating report:', error);
            // Error message is already handled in the API method
        } finally {
            setCreatingReport(false);
        }
    };return (
        <>
            <H5>Eksper Raporları</H5>
            <div style={{ height: "20px" }}></div>

            <Row>
                <Col md={4}>
                    <FormGroup>
                        <Label for="fileNumberSelect">Dosya No <span className="text-danger">*</span></Label>
                        <Input
                            id="fileNumberSelect"
                            type="select"
                            value={selectedFileNumber}
                            onChange={(e) => setSelectedFileNumber(e.target.value)}
                            disabled={!canEdit || generateReportAccess.disabled}
                        >
                            <option value="">Dosya numarası seçiniz...</option>
                            {getAvailableFileNumbers().map((fileOption) => (
                                <option key={fileOption.value} value={fileOption.value}>
                                    {fileOption.label} ({fileOption.fileTypeTitles.filter(title => !isLorFileType(title)).join(', ')})
                                </option>
                            ))}
                        </Input>
                        {getAvailableFileNumbers().length === 0 && (
                            <small className="text-muted">Henüz eksper raporu oluşturulabilecek dosya bulunmamaktadır. (Sadece Hak Mahrumiyeti içeren dosyalar için rapor oluşturulamaz.)</small>
                        )}
                    </FormGroup>
                </Col>
                <Col md={4}>
                    <FormGroup>
                        <Label for="templateSelect">Rapor Türü <span className="text-danger">*</span></Label>
                        <Input
                            id="templateSelect"
                            type="select"
                            value={selectedTemplate}
                            onChange={(e) => setSelectedTemplate(e.target.value)}
                            disabled={loadingTemplates || !canEdit || generateReportAccess.disabled}
                        >
                            <option value="">Rapor türü seçiniz...</option>
                            {reportTemplates.map((template) => (
                                <option key={template.id} value={template.id}>
                                    {template.title}
                                </option>
                            ))}
                        </Input>
                        {loadingTemplates && (
                            <small className="text-muted">Rapor şablonları yükleniyor...</small>
                        )}
                        {!loadingTemplates && reportTemplates.length === 0 && (
                            <small className="text-muted">Henüz rapor şablonu bulunmamaktadır.</small>
                        )}
                    </FormGroup>
                </Col>
                <Col md={4} className="d-flex align-items-end">
                    <FormGroup className="mb-0">
                        <Button 
                            color="primary" 
                            onClick={handleCreateReport}
                            disabled={!selectedFileNumber || !selectedTemplate || creatingReport || loadingTemplates || !canEdit || generateReportAccess.disabled}
                        >
                            {creatingReport ? 'Oluşturuluyor...' : 'Rapor Oluştur'}
                        </Button>
                    </FormGroup>
                </Col>
            </Row>
        </>
    );
};

export default ExpertReportForm;
